import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * Sticky contact button that appears after the user scrolls down.
 * - Mobile: compact circular icon button (bottom-left, to avoid Hamburger top-right).
 * - Desktop: pill with label "Kontakt" (bottom-right).
 * - Color accent depends on route (Consulting vs. Mensch im Fokus).
 * Opens the contact page independently of the device's email configuration.
 */
const StickyContactButton = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 400);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const accent = pathname.startsWith("/consulting")
    ? "bg-consulting hover:bg-consulting/90"
    : pathname.startsWith("/mensch-im-fokus")
      ? "bg-mif hover:bg-mif/90"
      : "bg-foreground hover:opacity-85";

  return (
    <Button asChild
      className={cn(
        "fixed z-40 text-primary-foreground shadow-lg transition-all duration-300",
        // Mobile: bottom-left circle (avoids hamburger top-right)
        "bottom-5 left-5 w-12 h-12 rounded-full flex items-center justify-center",
        // Desktop: bottom-right pill
        "md:left-auto md:right-6 md:bottom-6 md:w-auto md:h-auto md:rounded-full md:px-5 md:py-3 md:gap-2 md:inline-flex md:items-center md:text-sm md:font-medium",
        accent,
        visible && pathname !== "/kontakt/"
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none",
      )}
    >
      <Link to="/kontakt/" aria-label="Kontakt" tabIndex={visible && pathname !== "/kontakt/" ? 0 : -1}>
        <Mail className="w-5 h-5 md:w-4 md:h-4" />
        <span className="hidden md:inline">Kontakt</span>
      </Link>
    </Button>
  );
};

export default StickyContactButton;
